/* eslint-disable internal-rules/composable-consistent-naming */
import {
    ACTION_TYPES,
    ITEM_LOCATION_TYPES,
    ITEM_TOP_LEVEL,
    SPECIAL_ACTION_IDS,
} from '@/roadmaps/constants/roadmaps.constants';
import {
    getItemLocationTypeByLevel,
    getPendingForbiddenAction,
    getPendingResolveActionsForChildren,
    getPendingResolveActionsForSiblingsForMoveToBacklog,
    getPendingResolveActionsForUnNesting,
} from '@/roadmaps/helpers/interactions/roadmaps-common-interactions.helper';
import {
    calculateDurationInDays,
    calculateXFromDate,
    clampChildDatesToParent,
    shiftDateByDays,
} from '@/roadmaps/helpers/roadmaps-date.helper';
import { useDraggable } from '@/roadmaps/use/use-draggable';
import { getSubRowFromY } from '@/roadmaps/use/use-swimlanes';
import { useZoom } from '@/roadmaps/use/use-zoom';
/* eslint-disable no-restricted-imports */

import store from '@/vuex/store';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import { unref } from 'vue';

export function getPendingResizeActions (rootSource, resizeType, newRootSourceLocation) {
    const resizeAction = {
        id: rootSource.id,
        type: resizeType,
        updates: newRootSourceLocation,
        preview: true,
        targetItem: null,
    };

    const resolveActions = getPendingResolveActions({
        source: rootSource,
        newSourceLocation: newRootSourceLocation,
        preview: true,
    });

    const resolveChildActions = getPendingChildResolveActionsForResize(rootSource, resizeType, newRootSourceLocation);

    return keyBy([resizeAction, ...resolveActions, ...resolveChildActions], 'id');
}

function getPendingChildResolveActionsForResize (rootSource, resizeType, newRootSourceLocation) {
    const childItems = store.getters['roadmaps/items/itemsByParentId'](rootSource.id);

    return reduce(
        childItems,
        (accumulator, child) => {
            let updates;

            if (child.startDate === rootSource.startDate && child.endDate === rootSource.endDate) {
                if (resizeType === ACTION_TYPES.RESIZE_LEFT) {
                    updates = {
                        startDate: newRootSourceLocation.startDate,
                        endDate: rootSource.endDate,
                    };
                } else {
                    updates = {
                        startDate: rootSource.startDate,
                        endDate: newRootSourceLocation.endDate,
                    };
                }
            } else {
                const { childStartDate, childEndDate } = clampChildDatesToParent(
                    { startDate: child.startDate, endDate: child.endDate },
                    {
                        startDate: newRootSourceLocation?.startDate ?? rootSource.startDate,
                        endDate: newRootSourceLocation?.endDate ?? rootSource.endDate,
                    },
                );

                updates = {
                    startDate: childStartDate,
                    endDate: childEndDate,
                };
            }

            accumulator.push({
                id: child.id,
                type: ACTION_TYPES.RESOLVE,
                updates,
                preview: true,
                targetItem: null,
            });

            return accumulator;
        },
        [],
    );
}

export function getPendingDragActions (rootSource, newRootSourceLocation) {
    const dragAction = {
        id: rootSource.id,
        type: ACTION_TYPES.DRAG,
        updates: newRootSourceLocation,
        preview: true,
        targetItem: null,
    };

    const resolveActions = [];

    const unNestingResolveActions = getPendingResolveActionsForUnNesting(rootSource, newRootSourceLocation.parentId);
    resolveActions.push(...unNestingResolveActions);

    const hasChildren = Object.keys(store.getters['roadmaps/items/itemsByParentId'](rootSource.id)).length > 0;
    if (hasChildren) {
        const childResolveActions = getPendingResolveActionsForChildrenOfDraggedItem(rootSource, newRootSourceLocation);
        resolveActions.push(...childResolveActions);
    }

    const swimlaneResolves = getPendingResolveActions({
        source: rootSource,
        newSourceLocation: newRootSourceLocation,
        preview: false,
    });
    resolveActions.push(...swimlaneResolves);

    return keyBy([dragAction, ...resolveActions], 'id');
}

function getPendingResolveActions ({ source, newSourceLocation, preview }) {
    const collisionCandidates = getCollisionCandidates(source, newSourceLocation);

    const resolveActions = reduce(
        collisionCandidates,
        (accumulator, target) => {
            if (isColliding(source, newSourceLocation, target)) {
                const targetResolveAction = {
                    id: target.id,
                    type: ACTION_TYPES.RESOLVE,
                    updates: {
                        row: target.row + 1,
                    },
                    preview,
                    targetItem: null,
                };
                accumulator.push(targetResolveAction);

                const subsequentResolveActions = getPendingResolveActions({
                    source: target,
                    newSourceLocation: targetResolveAction.updates,
                    preview,
                });
                accumulator.push(...subsequentResolveActions);
            }

            return accumulator;
        },
        [],
    );

    return resolveActions;
}

function getPendingResolveActionsForChildrenOfDraggedItem (rootSource, newRootSourceLocation) {
    const childItems = store.getters['roadmaps/items/itemsByParentId'](rootSource.id);

    return reduce(
        childItems,
        (accumulator, child) => {
            let updates;

            if (rootSource.onBacklog) {
                updates = {
                    onBacklog: false,
                    startDate: newRootSourceLocation.startDate,
                    endDate: newRootSourceLocation.endDate,
                };
            } else {
                const daysMovedBy = calculateDurationInDays(rootSource.startDate, newRootSourceLocation.startDate);
                const childStartDate = shiftDateByDays({ date: child.startDate, daysToShift: daysMovedBy });
                const childEndDate = shiftDateByDays({ date: child.endDate, daysToShift: daysMovedBy });

                updates = {
                    startDate: childStartDate,
                    endDate: childEndDate,
                };
            }

            accumulator.push({
                id: child.id,
                type: ACTION_TYPES.RESOLVE,
                updates,
                preview: true,
                targetItem: null,
            });

            return accumulator;
        },
        [],
    );
}

export function getPendingGroupActions (rootSource, target, newRootSourceLocation, relativeCursorPos) {
    const sourceLevel = ITEM_LOCATION_TYPES[rootSource.type].level;
    const targetLevel = ITEM_LOCATION_TYPES[target.type].level;

    const canPromote = sourceLevel === targetLevel && targetLevel !== ITEM_TOP_LEVEL;
    const canNest = sourceLevel > targetLevel;
    if (!canPromote && !canNest) {
        return getPendingForbiddenAction(rootSource, target);
    }

    const unNestingResolveActions = getPendingResolveActionsForUnNesting(rootSource, target.id);

    if (canPromote) {
        const promoteActions = getPendingPromoteActions(rootSource, target);

        return keyBy([...unNestingResolveActions, ...promoteActions], 'id');
    }

    if (canNest) {
        const nestAction = getPendingNestAction(rootSource, target, newRootSourceLocation, relativeCursorPos);

        const childToAdd = Object.assign({}, rootSource, nestAction.updates);
        const newParentChildResolves = getPendingResolveActionsForChildren(target.id, childToAdd);

        return keyBy([...unNestingResolveActions, ...newParentChildResolves, nestAction], 'id');
    }
}

function getPendingPromoteActions (rootSource, target) {
    const sourceLevel = ITEM_LOCATION_TYPES[rootSource.type].level;
    const parentType = getItemLocationTypeByLevel(sourceLevel - 1);

    const parentAction = {
        id: SPECIAL_ACTION_IDS.PROMOTE_PARENT,
        type: ACTION_TYPES.PROMOTE_PARENT,
        updates: {
            parentId: target.parentId,
            parentType: target.parentType,
            type: parentType,
            startDate: target.startDate,
            endDate: target.endDate,
            row: target.row,
            onBacklog: false,
            isExpanded: false,
        },
        targetItem: null,
        preview: true,
    };

    const childSourceAction = {
        id: rootSource.id,
        type: ACTION_TYPES.PROMOTE_CHILD_SOURCE,
        updates: {
            parentType,
            startDate: target.startDate,
            endDate: target.endDate,
            row: 1,
        },
        preview: true,
        targetItem: target,
    };

    if (rootSource.onBacklog) {
        childSourceAction.updates.onBacklog = false;
    }

    const childTargetAction = {
        id: target.id,
        type: ACTION_TYPES.PROMOTE_CHILD_TARGET,
        updates: {
            parentType,
            row: 0,
        },
        preview: true,
        targetItem: null,
    };

    return [parentAction, childSourceAction, childTargetAction];
}

function getPendingNestAction (rootSource, target, newRootSourceLocation, relativeCursorPos) {
    const preview = target.isExpanded;
    const newChildRow = getSubRowFromY(target, relativeCursorPos.y);

    const { childStartDate, childEndDate } = clampChildDatesToParent(
        { startDate: newRootSourceLocation.startDate, endDate: newRootSourceLocation.endDate },
        { startDate: target.startDate, endDate: target.endDate },
    );

    const nestAction = {
        id: rootSource.id,
        type: ACTION_TYPES.NEST,
        updates: {
            parentId: target.id,
            parentType: target.type,
            startDate: childStartDate,
            endDate: childEndDate,
            row: newChildRow,
        },
        preview,
        targetItem: target,
    };
    if (rootSource.onBacklog) {
        nestAction.updates.onBacklog = false;
    }

    return nestAction;
}

export function isColliding (source, newSourceLocation, target) {
    if (source.id === target.id) return false;

    const { zoomLevel } = useZoom();
    const { startDate: roadmapStartDate } = store.getters['roadmaps/roadmaps/currentRoadmap'];

    const sourceLeft = calculateXFromDate({
        date: newSourceLocation?.startDate ?? source.startDate,
        roadmapStartDate,
        zoomLevel: unref(zoomLevel),
    });
    const sourceRight = calculateXFromDate({
        date: newSourceLocation?.endDate ?? source.endDate,
        roadmapStartDate,
        zoomLevel: unref(zoomLevel),
    });
    const targetLeft = calculateXFromDate({
        date: target.startDate,
        roadmapStartDate,
        zoomLevel: unref(zoomLevel),
    });
    const targetRight = calculateXFromDate({
        date: target.endDate,
        roadmapStartDate,
        zoomLevel: unref(zoomLevel),
    });

    const sourceIsLeftOfTarget = sourceRight <= targetLeft;
    const sourceIsRightOfTarget = sourceLeft >= targetRight;
    const isIntersecting = !(sourceIsLeftOfTarget || sourceIsRightOfTarget);

    const exactMatch = sourceLeft === targetLeft && sourceRight === targetRight;

    return isIntersecting || exactMatch;
}

function getCollisionCandidates (source, newSourceLocation) {
    const { draggedItem } = useDraggable();
    const swimlaneId = newSourceLocation.parentId ?? source.parentId;
    const row = newSourceLocation.row ?? source.row;
    const itemsInTheSameRow = store.getters['roadmaps/items/swimlaneItemsByRow'](swimlaneId, row);
    const itemsInTheSameRowWithoutDraggedItem = reject(itemsInTheSameRow, (item) => item.id === unref(draggedItem)?.id);

    return itemsInTheSameRowWithoutDraggedItem;
}

export function getPendingMoveToBacklogActions (rootSource) {
    const moveToBacklogAction = {
        id: rootSource.id,
        type: ACTION_TYPES.MOVE_TO_BACKLOG,
        updates: {
            parentId: '',
            parentType: ITEM_LOCATION_TYPES.BACKLOG.key,
            onBacklog: true,
            startDate: '',
            endDate: '',
            row: null,
        },
        targetItem: null,
        preview: true,
    };

    const resolveActions = getPendingResolveActionsForMoveToBacklog(rootSource);

    return keyBy([moveToBacklogAction, ...resolveActions], 'id');
}

function getPendingResolveActionsForMoveToBacklog (rootSource) {
    const resolveActions = [];

    const childItems = store.getters['roadmaps/items/itemsByParentId'](rootSource.id);
    if (childItems.length > 0) {
        const childResolves = map(childItems, (childItem) => {
            return {
                id: childItem.id,
                type: ACTION_TYPES.RESOLVE,
                updates: {
                    onBacklog: true,
                    startDate: '',
                    endDate: '',
                },
                targetItem: null,
                preview: true,
            };
        });

        resolveActions.push(...childResolves);
    }

    const previousSiblingResolveActions = getPendingResolveActionsForSiblingsForMoveToBacklog(rootSource);
    resolveActions.push(...previousSiblingResolveActions);

    return resolveActions;
}
